import { useDispatch } from 'react-redux';
import { Message } from '@blink/components/src/constants/messages';
import { WalletHelper } from '@blink/components/src/utils';

import { OperationStatusType } from '@/features/Modals/ui/OperationStatusModal';
import { useEthers } from '@/shared/hooks/useEthers';
import { showModalAction } from '@/shared/store/modals/actions';
import { ModalName } from '@/shared/store/modals/constants';
import { getAssetsToSupplyAction, getUsersSupplies } from '@/shared/store/supplies/actions';
import { withdrawalErrorAction } from '@/shared/store/errors/actions';
import { compareBigNumbers } from '@/shared/utils/helpers';
import { toast } from '@/shared/hooks/useToast';

type MakeWithdrawalProps = {
    amount: string;
    agreementAddress: string;
    marginAccountAddress: string;
    tokenAddress: string;
    totalPosition: string;
    poolBalance: string;
    decimals: number;
};

export const useVaultMarginAccountWithdrawal = () => {
    const dispatch = useDispatch();
    const { withdrawVault } = useEthers();

    const handleError = (e: any) => {
        dispatch(
            showModalAction({
                modalName: ModalName.OPERATION_STATUS_MODAL,
                modalProps: {
                    type: OperationStatusType.Error,
                    description: e.info?.error?.message || Message.DEFAULT_ERROR,
                },
            }),
        );
    };

    const makeWithdrawal = async ({
        amount,
        agreementAddress,
        marginAccountAddress,
        tokenAddress,
        totalPosition,
        poolBalance,
        decimals,
    }: MakeWithdrawalProps) => {
        const commonVariables = {
            amount: amount?.toString() || '0',
            poolAddress: agreementAddress,
            tokenAddress,
            marginAccountAddress,
            orderPlacedCallback: () =>
                dispatch(
                    showModalAction({
                        modalName: ModalName.OPERATION_STATUS_MODAL,
                        modalProps: {
                            type: OperationStatusType.Success,
                            description: Message.SUPPLY_ORDER_PLACED,
                        },
                    }),
                ),
            receiptReceivedCallback: (data: any) => {
                if (data.status === 1) {
                    toast({
                        title: Message.TRANSACTION_COMPLETED,
                        variant: 'success',
                    });
                    dispatch(getUsersSupplies());
                    dispatch(getAssetsToSupplyAction());

                    return;
                }

                throw data.error || Message.DEFAULT_ERROR;
            },
        };

        try {
            const result: number = compareBigNumbers(totalPosition, poolBalance, decimals);
            const min1: string = result < 0 ? totalPosition : poolBalance;

            const res: number = compareBigNumbers(min1, amount!.toString(), decimals);
            const amountToWithdraw = res < 0 ? min1 : amount!.toString();

            await withdrawVault({
                ...commonVariables,
                amount: amountToWithdraw,
            });

            return;
        } catch (e: any) {
            const badMessage = WalletHelper.messageInCaseOfNotCorrectNetwork(e);

            dispatch(
                withdrawalErrorAction({
                    message: badMessage || e.info?.error?.message || Message.DEFAULT_ERROR,
                }),
            );

            handleError(e);
        }
    };

    return {
        makeWithdrawal,
    };
};
